/**
 * Feature E04-F01 — Inbox split-view (E04-F01-T04 layout + preview, E04-F01-T05
 * pagination/search wiring).
 *
 * 1:1 port of the legacy SPA `../EER app/public/assets/js/inbox.js`
 * (renderInbox / selectEmail / _buildEmailPreviewHtml / loadEmails pagination /
 * _filterEmailsBySearch). Two panels: a scrollable list of incoming email cards
 * on the left, a full preview on the right. All display/pagination/search rules
 * live in `./inboxModel.ts` (unit-tested without a DOM); this component is the
 * thin React shell that composes them with the `./inboxApi.ts` Query hooks.
 *
 * Parity behaviours preserved:
 *   - cards show sender, date, subject||'(no subject)', de-HTML'd preview, the
 *     classification badge, a paperclip when attachments exist, and client/role
 *     for linked emails (T04);
 *   - clicking a card makes it active and renders the preview; an email without a
 *     full `body` triggers a single-email fetch and shows "Loading full text…"
 *     (the spinner) until it arrives, then the preview re-renders (T04);
 *   - the feed shows ONLY incoming mail — `direction === 'out'` is filtered out
 *     client-side, on load AND in search (T04/T05);
 *   - a debounced client search narrows the loaded list over the joined haystack
 *     (T05);
 *   - filter tabs reload the feed (append=false) and highlight the active tab;
 *   - "Load more" is visible only while the last page came back full (T05);
 *   - sender/subject/preview/filenames are React-escaped (no innerHTML).
 *
 * Wire-up (the serial Wire pass mounts this as the `inbox` view body in the shell
 * — see `shell/AppShell.tsx` where `ViewPlaceholder` currently renders for
 * `view === 'inbox'`):
 *   import { InboxView } from '../inbox/InboxView.js';
 *   …
 *   {active ? (view === 'inbox' ? <InboxView /> : <ViewPlaceholder view={view} />) : null}
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isTerminalInboxCheckStatus, type EmailDTO } from '@eer/shared';
import {
  classificationBadge,
  filterEmailsBySearch,
  incomingOnly,
  hasAttachments,
  emailAttachments,
  cardSender,
  senderDisplay,
  previewRecipient,
  subjectLabel,
  stripHtmlPreview,
  previewBodyLines,
  isBodyLoading,
  formatAttachmentSize,
  formatCardDate,
  formatPreviewDate,
  attachmentIcon,
  INBOX_FILTER_TABS,
  type InboxBadgeTone,
} from './inboxModel.js';
import { useInboxEmails, useEmailFullBody, type InboxListFilter } from './inboxApi.js';
import {
  useCreateProjectFromEmail,
  useUnlinkEmail,
  useCheckInbox,
  useInboxCheckStatus,
} from './inboxActionsApi.js';
import { qk } from '../api/queryKeys.js';
import { LinkEmailDialog } from './LinkEmailDialog.js';
import { ReplyEmailDialog } from './ReplyEmailDialog.js';
import { errorMessage } from '../api/client.js';
import { useToast } from '../ui/ToastContext.js';
import { useConfirm } from '../ui/ConfirmDialog.js';
import { routeToPath, routeToState } from '../router/routes.js';

/** Debounce a value by `delay` ms (legacy inbox search was debounced ~250ms). */
function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/** Tailwind classes for the classification badge tones (green / amber / gray). */
const BADGE_TONE_CLASS: Record<InboxBadgeTone, string> = {
  linked: 'bg-green-100 text-green-700',
  'new-project': 'bg-amber-100 text-amber-700',
  muted: 'bg-gray-100 text-gray-600',
};

/**
 * Navigate to a project's detail route from the inbox WITHOUT a router prop.
 *
 * The shell (`AppShell.renderView`) mounts `<InboxView />` with no props, but it
 * DOES listen for `popstate` via `useRouter`. So we reuse the router's own pure
 * serializers (`routeToPath` / `routeToState`) to push the exact same history
 * entry the shell expects, then dispatch `popstate` so `useRouter` restores the
 * project detail view — the same `/project/:id` contract used everywhere else.
 * When an explicit `onOpenProject` prop is provided (tests / future hosts) it
 * takes precedence.
 */
function openProjectViaHistory(projectId: number): void {
  if (typeof window === 'undefined') return;
  const route = { view: 'project' as const, id: String(projectId) };
  window.history.pushState(routeToState(route), '', routeToPath(route));
  window.dispatchEvent(new PopStateEvent('popstate', { state: routeToState(route) }));
}

// ---------------------------------------------------------------------------
// list card
// ---------------------------------------------------------------------------

interface CardProps {
  readonly email: EmailDTO;
  readonly active: boolean;
  readonly onSelect: (email: EmailDTO) => void;
}

function EmailCard({ email, active, onSelect }: CardProps): React.JSX.Element {
  const badge = classificationBadge(email);
  const linked = email.project_id != null;
  return (
    <button
      type="button"
      data-testid={`inbox-card-${email.id}`}
      data-email-id={email.id}
      onClick={() => onSelect(email)}
      className={`w-full border-b border-border p-3 text-left transition hover:bg-slate-50 ${
        active ? 'border-l-2 border-l-primary bg-blue-50' : ''
      }`}
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="truncate text-sm font-semibold text-text-strong">{cardSender(email)}</span>
        <span className="shrink-0 text-xs text-gray-400">{formatCardDate(email.received_at)}</span>
      </div>
      <div className="mb-1 flex items-center gap-1.5">
        {badge.label ? (
          <span
            data-testid="inbox-card-badge"
            className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${BADGE_TONE_CLASS[badge.tone]}`}
          >
            {badge.label}
          </span>
        ) : null}
        {hasAttachments(email) ? (
          <i
            data-testid="inbox-card-paperclip"
            className="fa-solid fa-paperclip text-xs text-gray-400"
            aria-label="Has attachments"
          />
        ) : null}
      </div>
      <div className="mb-1 truncate text-sm text-slate-700">{subjectLabel(email)}</div>
      <div className="truncate text-xs text-gray-400">{stripHtmlPreview(email.body_preview)}</div>
      {linked ? (
        <div className="mt-1 truncate text-xs text-blue-500">
          {email.client_name ?? ''} / {email.project_role ?? ''}
        </div>
      ) : null}
    </button>
  );
}

// ---------------------------------------------------------------------------
// preview panel
// ---------------------------------------------------------------------------

interface PreviewProps {
  readonly email: EmailDTO;
  /** Open the link-to-project picker dialog ("Link to Project"). */
  readonly onLink: () => void;
  /** Open the reply dialog ("Reply"). */
  readonly onReply: () => void;
  /** Create a project from this email ("Create Project" — confirm + spinner). */
  readonly onCreate: () => void;
  /** Unlink this email from its project ("Unlink" — confirm + spinner). */
  readonly onUnlink: () => void;
  /** Open the linked project's detail ("Open Project"). */
  readonly onOpenProject: () => void;
  /** True while the create-project request is in flight. */
  readonly creating: boolean;
  /** True while the unlink request is in flight. */
  readonly unlinking: boolean;
}

/**
 * Preview action row — 1:1 with legacy `_buildEmailPreviewHtml` action set:
 *   - UNLINKED: Link to Project · Create Project · Reply
 *   - LINKED:   Open Project · Unlink · Reply
 * Reply is always present. Create/Unlink confirm() before acting (in the host)
 * and show a spinner label ("Creating…" / "Unlinking…") while pending.
 */
function PreviewActions({
  linked,
  onLink,
  onReply,
  onCreate,
  onUnlink,
  onOpenProject,
  creating,
  unlinking,
}: Omit<PreviewProps, 'email'> & { readonly linked: boolean }): React.JSX.Element {
  return (
    <div data-testid="inbox-preview-actions" className="mb-4 flex flex-wrap gap-2">
      {linked ? (
        <>
          <button
            type="button"
            data-testid="inbox-action-open"
            onClick={onOpenProject}
            className="rounded bg-slate-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
          >
            <i className="fa-solid fa-folder-open mr-1" aria-hidden />
            Open Project
          </button>
          <button
            type="button"
            data-testid="inbox-action-unlink"
            onClick={onUnlink}
            disabled={unlinking}
            className="rounded border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-60"
          >
            <i
              className={`fa-solid ${unlinking ? 'fa-spinner fa-spin' : 'fa-link-slash'} mr-1`}
              aria-hidden
            />
            {unlinking ? 'Unlinking…' : 'Unlink'}
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            data-testid="inbox-action-link"
            onClick={onLink}
            className="rounded bg-blue-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-600"
          >
            <i className="fa-solid fa-link mr-1" aria-hidden />
            Link to Project
          </button>
          <button
            type="button"
            data-testid="inbox-action-create"
            onClick={onCreate}
            disabled={creating}
            className="rounded bg-amber-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-600 disabled:opacity-60"
          >
            <i
              className={`fa-solid ${creating ? 'fa-spinner fa-spin' : 'fa-plus'} mr-1`}
              aria-hidden
            />
            {creating ? 'Creating…' : 'Create Project'}
          </button>
        </>
      )}
      <button
        type="button"
        data-testid="inbox-action-reply"
        onClick={onReply}
        className="rounded border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
      >
        <i className="fa-solid fa-reply mr-1" aria-hidden />
        Reply
      </button>
    </div>
  );
}

function EmailPreview({
  email,
  onLink,
  onReply,
  onCreate,
  onUnlink,
  onOpenProject,
  creating,
  unlinking,
}: PreviewProps): React.JSX.Element {
  const attachments = emailAttachments(email);
  const loading = isBodyLoading(email);
  const lines = previewBodyLines(email);
  const linked = email.project_id != null;
  return (
    <div data-testid="inbox-preview" className="flex h-full flex-col overflow-y-auto p-6">
      <h3 data-testid="inbox-preview-subject" className="mb-2 text-lg font-bold text-slate-800">
        {subjectLabel(email)}
      </h3>
      <div className="mb-1 text-sm text-slate-600">
        <span className="font-medium">From:</span>{' '}
        <span data-testid="inbox-preview-from">{senderDisplay(email)}</span>
      </div>
      <div className="text-xs text-gray-400">To: {previewRecipient(email)}</div>
      <div className="mb-4 text-xs text-gray-400">{formatPreviewDate(email.received_at)}</div>

      <PreviewActions
        linked={linked}
        onLink={onLink}
        onReply={onReply}
        onCreate={onCreate}
        onUnlink={onUnlink}
        onOpenProject={onOpenProject}
        creating={creating}
        unlinking={unlinking}
      />

      {attachments.length > 0 ? (
        <div
          className="mb-4 rounded border border-border p-3"
          data-testid="inbox-preview-attachments"
        >
          <div className="mb-2 text-xs font-semibold text-slate-600">
            Attachments ({attachments.length})
          </div>
          <ul className="space-y-1">
            {attachments.map((a, i) => (
              <li
                key={`${a.filename}-${i}`}
                className="flex items-center gap-2 text-sm text-slate-700"
              >
                <i className={`fa-solid ${attachmentIcon(a.contentType)}`} aria-hidden />
                <span className="truncate">{a.filename}</span>
                <span className="ml-auto shrink-0 text-xs text-gray-400">
                  {formatAttachmentSize(a.size)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div data-testid="inbox-preview-body" className="whitespace-pre-wrap text-sm text-slate-700">
        {lines.map((line, i) => (
          <span key={i}>
            {line}
            {i < lines.length - 1 ? <br /> : null}
          </span>
        ))}
        {loading ? (
          <p data-testid="inbox-preview-loading" className="mt-2 text-xs text-gray-400">
            <i className="fa-solid fa-spinner fa-spin mr-1" aria-hidden />
            Loading full text...
          </p>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// split-view shell
// ---------------------------------------------------------------------------

export interface InboxViewProps {
  /**
   * Open a project's detail view by id ("Open Project" / create-then-open). The
   * shell mounts `<InboxView />` with no props, so this is optional — when absent
   * the inbox navigates through the shared router's `history` contract directly
   * (`openProjectViaHistory`, which `useRouter` restores via `popstate`).
   */
  readonly onOpenProject?: (projectId: number) => void;
  /**
   * Pre-select a specific email on mount (E04-F02-T08: the project header's
   * "Created from email" link carries the source email's id through the route
   * state — `AppShell` reads `route.id` for the `inbox` view and passes it here
   * so the user lands on THAT email, not just the generic Inbox list).
   */
  readonly initialSelectedEmailId?: number | null;
}

export function InboxView({
  onOpenProject,
  initialSelectedEmailId = null,
}: InboxViewProps = {}): React.JSX.Element {
  const [classification, setClassification] = useState<string>('all');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search);
  const [selectedId, setSelectedId] = useState<number | null>(initialSelectedEmailId);

  // Which dialog is open for the currently previewed email (legacy showLink/showReply).
  const [linkOpen, setLinkOpen] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);

  const toast = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const queryClient = useQueryClient();
  const createProject = useCreateProjectFromEmail();
  const unlink = useUnlinkEmail();
  const checkInbox = useCheckInbox();

  // "Check Mail" completion indicator (E04-F01-T09): the POST only ACKs the enqueue,
  // so we hold the returned BullMQ jobId and poll GET /inbox/check/:jobId/status
  // until terminal. `checkJobId !== null` == a check is still running (drives the
  // button's "Checking…" state alongside the mutation's own pending window).
  const [checkJobId, setCheckJobId] = useState<string | null>(null);
  const checkStatus = useInboxCheckStatus(checkJobId);
  const isCheckingMail = checkInbox.isPending || checkJobId !== null;

  // React to the poll reaching a terminal state: refetch the feed NOW that the
  // worker finished (this is the real completion signal, not a blind timer), raise
  // a done/failed toast, and stop polling. `unknown` means the job already
  // completed and was auto-cleaned — treat as done.
  useEffect(() => {
    const status = checkStatus.data?.status;
    if (checkJobId === null || !status || !isTerminalInboxCheckStatus(status)) return;
    void queryClient.invalidateQueries({ queryKey: qk.emails.all });
    if (status === 'failed') toast.error('Mail check failed');
    else toast.success('Mail check complete');
    setCheckJobId(null);
  }, [checkStatus.data?.status, checkJobId, queryClient, toast]);

  const filter: InboxListFilter = useMemo(() => ({ classification }), [classification]);
  const feed = useInboxEmails(filter);

  // Flatten the infinite-query pages into a single list, then drop outgoing mail
  // (the inbox shows incoming only — applied even before search, T04/T05).
  const allEmails: EmailDTO[] = useMemo(
    () => incomingOnly((feed.data?.pages ?? []).flatMap((p) => p.emails)),
    [feed.data],
  );

  // Client search over the loaded rows (debounced). Empty query → incoming list.
  const visibleEmails = useMemo(
    () => filterEmailsBySearch(allEmails, debouncedSearch),
    [allEmails, debouncedSearch],
  );

  // The currently selected row from the loaded list (carries body_preview only).
  const selectedListEmail = useMemo(
    () => allEmails.find((e) => e.id === selectedId) ?? null,
    [allEmails, selectedId],
  );

  // A single-email full-body fetch fires only when a row is selected and its full
  // body is not present yet (list rows carry body_preview only — T04 dozagruzka).
  const needsBody = selectedListEmail != null && isBodyLoading(selectedListEmail);
  const fullBody = useEmailFullBody(selectedId, needsBody);

  // The email shown in the preview: the freshly-fetched full row if available,
  // else the list row (which shows the spinner while the body loads).
  const previewEmail: EmailDTO | null = fullBody.data ?? selectedListEmail;

  // Reset the selection when the filter changes (the feed reloads, append=false).
  const onSelectTab = (value: string): void => {
    setClassification(value);
    setSelectedId(null);
  };

  // "Open Project": use the host callback when provided, else the router history
  // contract (the shell has no prop but does listen for popstate — legacy openProject).
  const openProject = (projectId: number): void => {
    if (onOpenProject) onOpenProject(projectId);
    else openProjectViaHistory(projectId);
  };

  // "Create Project" — confirm(), then create_from_email → toast → open the new
  // project (legacy createProjectFromEmail). The spinner label is driven by the
  // mutation's pending state.
  const handleCreate = async (): Promise<void> => {
    if (!previewEmail) return;
    // Styled in-app confirm (design system) — not the native window.confirm (E04-F02-T09).
    const ok = await confirm({
      title: 'Create project',
      message: 'Create a new project from this email?',
      confirmLabel: 'Create',
    });
    if (!ok) return;
    createProject.mutate(previewEmail.id, {
      onSuccess: (res) => {
        toast.success('Project created');
        openProject(res.project_id);
      },
      onError: (err) => toast.error(err.detail.message || 'Error creating project'),
    });
  };

  // "Unlink" — confirm(), then unlink_from_project → toast (legacy
  // unlinkEmailFromProject). The feed reloads via the mutation invalidation.
  const handleUnlink = async (): Promise<void> => {
    if (!previewEmail) return;
    // Destructive → danger-variant styled confirm (E04-F02-T09).
    const ok = await confirm({
      title: 'Unlink email',
      message: 'Unlink email from project?',
      confirmLabel: 'Unlink',
      variant: 'danger',
    });
    if (!ok) return;
    unlink.mutate(previewEmail.id, {
      onSuccess: () => toast.success('Email unlinked from project'),
      onError: (err) => toast.error(err.detail.message || 'Unlink error'),
    });
  };

  // "Check Mail" — trigger the email checker for ALL active mailboxes (legacy
  // app.checkMail). The success/error toast reflects the enqueue ack and fires
  // immediately; the feed refetch is deferred inside the mutation (the checker
  // runs async in a BullMQ worker — see CHECK_INBOX_REFRESH_DELAY_MS, legacy 5s
  // setTimeout(loadEmails) parity). The error path funnels through `errorMessage`
  // because the mutationFn can reject with a ZodError (no `.detail`) on a malformed
  // 2xx body — reading `err.detail.message` blindly would crash (the admin
  // Settings white-screen incident).
  const handleCheckMail = (): void => {
    checkInbox.mutate(undefined, {
      onSuccess: (res) => {
        // Async path: remember the jobId so the poll can report completion (the
        // terminal toast + feed refetch fire from the useEffect above). The ack
        // toast still fires immediately — it is accurate and instant.
        if (res.jobId) setCheckJobId(res.jobId);
        toast.success(res.message || 'Mail check started');
      },
      onError: (err) => toast.error(errorMessage(err, 'Mail check error')),
    });
  };

  const inputRef = useRef<HTMLInputElement>(null);
  const isEmpty = !feed.isLoading && visibleEmails.length === 0;

  return (
    <div data-testid="view-inbox-body" className="flex h-full flex-col">
      {/* Top bar: filter tabs (v1 pill order All / Unclassified / New Projects /
          Linked) + Check Mail — legacy `index.php` #inbox-filters bar (FP-08). */}
      <div className="flex items-center justify-between gap-2 border-b border-border p-3">
        <div data-testid="inbox-filters" className="flex flex-wrap gap-2">
          {INBOX_FILTER_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              data-testid={`inbox-filter-${tab.value}`}
              aria-pressed={classification === tab.value}
              onClick={() => onSelectTab(tab.value)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                classification === tab.value
                  ? 'bg-slate-800 text-white'
                  : 'bg-white text-gray-500 hover:bg-slate-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {/* "Check Mail" — legacy app.checkMail (index.php filters bar). */}
        <button
          type="button"
          data-testid="inbox-check-mail-button"
          onClick={handleCheckMail}
          disabled={isCheckingMail}
          className="shrink-0 rounded border border-green-200 bg-green-100 px-3 py-1 text-xs text-green-700 transition hover:bg-green-200 disabled:opacity-60"
        >
          <i
            className={`fa-solid ${isCheckingMail ? 'fa-spinner fa-spin' : 'fa-rotate'} mr-1`}
            aria-hidden
          />
          {isCheckingMail ? 'Checking…' : 'Check Mail'}
        </button>
      </div>

      {/* Split view: search + list (left) · preview (right). v1 keeps the search
          INSIDE the list panel, below the top filters bar. */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: search + list */}
        <div className="flex w-2/5 max-w-md flex-col border-r border-border">
          <div className="border-b border-border p-3">
            <input
              ref={inputRef}
              data-testid="inbox-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search emails…"
              className="w-full rounded border border-border px-3 py-1.5 text-sm focus:border-primary focus:outline-none"
            />
          </div>

          <div data-testid="inbox-list" className="flex-1 overflow-y-auto">
            {feed.isLoading ? (
              <div className="p-4 text-sm text-gray-400">Loading…</div>
            ) : isEmpty ? (
              <div data-testid="inbox-empty" className="p-8 text-center text-sm text-gray-400">
                No emails to show.
              </div>
            ) : (
              visibleEmails.map((email) => (
                <EmailCard
                  key={email.id}
                  email={email}
                  active={email.id === selectedId}
                  onSelect={(e) => setSelectedId(e.id)}
                />
              ))
            )}

            {/* Load more — visible only while the last page came back full (T05). */}
            {feed.hasNextPage ? (
              <button
                type="button"
                data-testid="inbox-load-more"
                disabled={feed.isFetchingNextPage}
                onClick={() => void feed.fetchNextPage()}
                className="w-full p-3 text-center text-sm text-primary hover:bg-slate-50 disabled:opacity-50"
              >
                <i className="fa-solid fa-arrow-down mr-1" aria-hidden />
                {feed.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            ) : null}
          </div>
        </div>

        {/* Right: preview */}
        <div className="flex-1">
          {previewEmail ? (
            <EmailPreview
              email={previewEmail}
              onLink={() => setLinkOpen(true)}
              onReply={() => setReplyOpen(true)}
              onCreate={() => void handleCreate()}
              onUnlink={() => void handleUnlink()}
              onOpenProject={() =>
                previewEmail.project_id != null && openProject(previewEmail.project_id)
              }
              creating={createProject.isPending}
              unlinking={unlink.isPending}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-gray-400">
              Select an email to preview.
            </div>
          )}
        </div>
      </div>

      {/* Dialogs — mounted for the currently previewed email (legacy show*Dialog). */}
      {previewEmail ? (
        <>
          <LinkEmailDialog
            email={previewEmail}
            open={linkOpen}
            onClose={() => setLinkOpen(false)}
            onOpenProject={(id) => {
              setLinkOpen(false);
              openProject(id);
            }}
          />
          <ReplyEmailDialog
            email={previewEmail}
            open={replyOpen}
            onClose={() => setReplyOpen(false)}
          />
        </>
      ) : null}

      {/* Styled yes/no confirm for Create/Unlink — replaces window.confirm (E04-F02-T09). */}
      {confirmDialog}
    </div>
  );
}

export default InboxView;
